import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Check, Clock, ShieldCheck, Target, Users } from "lucide-react";
import { Reveal, MaskLines } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { ZIA } from "@/lib/data";
import zrmLogo from "@/assets/brand/zrm_mark_color.png";

export default function ZiaRisk() {
  return (
    <>
      {/* Hero */}
      <section className="relative bg-cream grain pt-36 pb-20 md:pt-48 md:pb-28">
        <div className="container-editorial relative z-10">
          <Reveal>
            <Link
              to="/family"
              data-testid="zia-back-link"
              className="inline-flex items-center gap-2 eyebrow text-[0.62rem] mb-10 text-blue/60 hover:text-amber-deep transition-colors"
            >
              <ArrowLeft size={14} /> Family of Companies
            </Link>
          </Reveal>
          <Reveal delay={0.05}>
            <img
              src={zrmLogo}
              alt="Zia Risk Management"
              data-testid="zia-logo"
              className="h-28 md:h-36 w-auto mb-8"
            />
          </Reveal>
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck size={20} className="text-amber-deep" />
            <p className="eyebrow text-amber-deep">{ZIA.eyebrow}</p>
          </div>
          <h1 className="font-serif font-bold text-5xl md:text-7xl lg:text-8xl text-blue leading-[0.98] tracking-tight">
            <MaskLines lines={[ZIA.title]} delay={0.1} />
            <MaskLines lines={[ZIA.titleItalic]} className="italic text-amber-deep" delay={0.26} />
          </h1>
          <Reveal delay={0.5}>
            <p className="font-serif italic text-2xl md:text-3xl mt-8 max-w-2xl text-blue/75">{ZIA.tagline}</p>
          </Reveal>
        </div>
      </section>

      {/* Problem + who */}
      <section className="bg-cream py-20 md:py-28">
        <div className="container-editorial grid md:grid-cols-2 gap-12 md:gap-20">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <Target size={18} className="text-amber-deep" />
              <p className="eyebrow text-amber-deep">The Problem</p>
            </div>
            <p className="font-serif font-bold text-2xl md:text-3xl text-blue leading-snug">{ZIA.problem}</p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="flex items-center gap-3 mb-6">
              <Users size={18} className="text-amber-deep" />
              <p className="eyebrow text-amber-deep">Who It's For</p>
            </div>
            <p className="font-body text-lg text-charcoal/75 leading-relaxed">{ZIA.whoFor}</p>
          </Reveal>
        </div>
      </section>

      {/* Deliverables */}
      <section data-testid="zia-deliverables" className="py-24 md:py-32 grain relative bg-blue text-cream">
        <div className="container-editorial relative z-10">
          <Reveal>
            <p className="eyebrow text-amber-bright mb-4">What Zia Protects</p>
            <h2 className="font-serif font-bold text-4xl md:text-5xl text-cream mb-14 max-w-2xl">
              Safeguards, <span className="italic text-amber-bright">built in from day one.</span>
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-px">
            {ZIA.deliverables.map((d, i) => (
              <Reveal key={i} delay={(i % 6) * 0.05}>
                <div className="flex items-start gap-6 py-6 border-b border-cream/12">
                  <span className="font-serif text-2xl text-amber-bright/70 w-10 shrink-0 leading-tight">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <Check size={18} className="text-amber-bright mt-1.5 shrink-0" />
                  <p className="font-body text-cream/85 leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline + value */}
      <section className="bg-cream py-20 md:py-28">
        <div className="container-editorial grid md:grid-cols-2 gap-6">
          <Reveal className="bg-blue text-cream p-10 md:p-14">
            <Clock size={24} className="text-amber-bright mb-6" />
            <p className="eyebrow text-amber-bright mb-4">Cadence</p>
            <p className="font-serif font-bold text-3xl md:text-4xl text-cream leading-tight">{ZIA.timeline}</p>
          </Reveal>
          <Reveal delay={0.1} className="border border-blue/15 p-10 md:p-14 bg-cream">
            <ShieldCheck size={24} className="text-amber-deep mb-6" />
            <p className="eyebrow text-amber-deep mb-4">The Role</p>
            <p className="font-serif font-bold text-2xl md:text-3xl text-blue leading-snug">{ZIA.valueLine}</p>
            <div className="mt-8 inline-flex items-center bg-blue text-cream px-6 py-3">
              <span className="eyebrow text-[0.62rem] text-amber-bright">{ZIA.savings}</span>
            </div>
            <p className="font-body text-sm text-slate mt-5">Scope and coverage confirmed on a call.</p>
          </Reveal>
        </div>
      </section>

      {/* Reframe */}
      <section className="py-24 md:py-36 grain relative bg-blue-deep text-cream">
        <div className="container-editorial relative z-10 text-center max-w-4xl mx-auto">
          <Reveal>
            <p className="eyebrow text-amber-bright mb-10">Consider This</p>
            <p className="font-serif italic text-3xl md:text-5xl text-cream leading-snug">&ldquo;{ZIA.reframe}&rdquo;</p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contact"
              data-testid="zia-cta"
              className="mt-12 inline-flex items-center gap-2 bg-amber-deep text-cream px-9 py-4 eyebrow text-xs hover:bg-cream hover:text-blue transition-colors"
            >
              Protect What You're Building <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
